import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle } from 'lucide-react';
import { TokenInfo, ClauseInfo, NounChunkInfo, SentenceInfo } from '../../types';
import { isProcessToken } from '../../utils/groupingRules';
import { useSoundHandlers } from '../../hooks/useSoundEffects';

interface Step1Props {
  tokens: TokenInfo[];
  clauses: ClauseInfo[];
  nounChunks: NounChunkInfo[];
  sentences: SentenceInfo[];
  onComplete: (score: number) => void;
  isCompleted: boolean;
}

// 过程短语结构（用于答案展示）
interface ProcessPhrase {
  text: string;
  indices: number[];
}

// 将连续的过程 tokens（同一小句层级）分组为短语
function groupProcessTokens(tokens: TokenInfo[]): ProcessPhrase[] {
  const phrases: ProcessPhrase[] = [];
  let currentGroup: TokenInfo[] = [];
  let currentClsLevel: number | null = null;
  
  const pushGroup = () => {
    if (currentGroup.length > 0) {
      phrases.push({
        text: currentGroup.map(t => t.text).join(' '),
        indices: currentGroup.map(t => t.index_doc),
      });
      currentGroup = [];
      currentClsLevel = null;
    }
  };
  
  tokens.forEach((token) => {
    const isProcess = isProcessToken(token, tokens);
    
    if (isProcess) {
      if (currentGroup.length === 0) {
        currentGroup.push(token);
        currentClsLevel = token.cls_level;
      } else if (token.cls_level === currentClsLevel) {
        currentGroup.push(token);
      } else {
        pushGroup();
        currentGroup.push(token);
        currentClsLevel = token.cls_level;
      }
    } else {
      pushGroup();
    }
  });
  
  pushGroup();
  return phrases;
}

export default function Step1Process({ 
  tokens, 
  onComplete, 
}: Step1Props) {
  // 学生选中的 token 索引（单词级别）
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [validated, setValidated] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    correct: number[];
    missed: number[];
    extra: number[];
  } | null>(null);
  const { playClick } = useSoundHandlers();

  // 正确的过程 token 索引
  const correctIndices = useMemo(() => {
    return tokens
      .filter(t => isProcessToken(t, tokens))
      .map(t => t.index_doc);
  }, [tokens]);

  // 正确的过程短语（用于答案展示）
  const correctPhrases = useMemo(() => groupProcessTokens(tokens), [tokens]);

  const handleTokenClick = (index: number) => {
    if (validated) return;
    const token = tokens.find(t => t.index_doc === index);
    if (!token || token.pos === 'PUNCT' || token.pos === 'SPACE') return;
    
    playClick(); // 播放选词音效
    
    setSelectedIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleValidate = () => {
    const selected = Array.from(selectedIndices);
    const correct = selected.filter(idx => correctIndices.includes(idx));
    const extra = selected.filter(idx => !correctIndices.includes(idx));
    const missed = correctIndices.filter(idx => !selected.includes(idx));
    
    const score = correctIndices.length > 0 
      ? correct.length / correctIndices.length 
      : 0;
    
    setValidationResult({ correct, missed, extra });
    setValidated(true);
    onComplete(score);
  };

  const getTokenStatus = (index: number): 'correct' | 'wrong' | 'missed' | null => {
    if (!validationResult) return null;
    if (validationResult.correct.includes(index)) return 'correct';
    if (validationResult.extra.includes(index)) return 'wrong';
    if (validationResult.missed.includes(index)) return 'missed';
    return null;
  };

  const handleReset = () => {
    setSelectedIndices(new Set());
    setValidated(false);
    setValidationResult(null);
  };

  // 将遗漏/多选的索引转换为短语显示
  const getMissedPhrases = (): string[] => {
    if (!validationResult) return [];
    // 找出哪些完整短语有遗漏的词
    return correctPhrases
      .filter(phrase => phrase.indices.some(idx => validationResult.missed.includes(idx)))
      .map(phrase => phrase.text);
  };

  const getExtraPhrases = (): string[] => {
    if (!validationResult) return [];
    // 将多选的单词分组显示
    const extraTokens = tokens.filter(t => validationResult.extra.includes(t.index_doc));
    return extraTokens.map(t => t.text);
  };

  return (
    <div>
      {/* Token 展示区 - 单词级别点击 */}
      <div className="p-6 rounded-lg bg-cyber-darker/50 border border-cyan-500/10 mb-6">
        <div className="flex flex-wrap gap-1 text-xl leading-loose font-mono items-center">
          {tokens.map((token) => {
            const isPunct = token.pos === 'PUNCT' || token.pos === 'SPACE';
            const isSelected = selectedIndices.has(token.index_doc);
            const status = getTokenStatus(token.index_doc);
            const isCorrectProcess = correctIndices.includes(token.index_doc);
            
            // 标点符号直接渲染
            if (isPunct) {
              return (
                <span key={token.id} className="text-gray-500 mx-0.5">
                  {token.text}
                </span>
              );
            }
            
            // 获取状态样式
            const getStatusClass = () => {
              switch (status) {
                case 'correct':
                  return 'bg-red-500/20 border-red-500 text-red-300';
                case 'wrong':
                  return 'bg-orange-500/20 border-orange-500 text-orange-300';
                case 'missed':
                  return 'bg-yellow-500/20 border-yellow-500 text-yellow-300';
                default:
                  return isSelected 
                    ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200 shadow-[0_0_10px_rgba(0,240,255,0.3)]'
                    : 'border-transparent hover:bg-cyan-500/10';
              }
            };
            
            return (
              <motion.span
                key={token.id}
                whileHover={!validated ? { scale: 1.05 } : undefined}
                whileTap={!validated ? { scale: 0.95 } : undefined}
                onClick={() => handleTokenClick(token.index_doc)}
                className={`
                  px-2 py-1 rounded-lg border cursor-pointer
                  transition-all duration-200
                  ${getStatusClass()}
                  ${validated ? 'cursor-default' : ''}
                  ${isCorrectProcess && validated ? 'font-semibold' : ''}
                `}
              >
                {token.text}
              </motion.span>
            );
          })}
        </div>
      </div>

      {/* 统计和操作 */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          已选择 <span className="text-cyan-400 font-mono">{selectedIndices.size}</span> 个词
          {validated && validationResult && (
            <span className="ml-4">
              正确 <span className="text-green-400">{validationResult.correct.length}</span> / 
              遗漏 <span className="text-yellow-400">{validationResult.missed.length}</span> / 
              多选 <span className="text-red-400">{validationResult.extra.length}</span>
            </span>
          )}
        </div>

        <div className="flex gap-3">
          {validated ? (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleReset}
              className="cyber-button"
            >
              重新选择
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleValidate}
              disabled={selectedIndices.size === 0}
              className="cyber-button-primary disabled:opacity-50"
            >
              验证答案
            </motion.button>
          )}
        </div>
      </div>

      {/* 验证结果反馈 */}
      {validated && validationResult && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 space-y-4"
        >
          {/* 正确答案展示 - 以短语形式 */}
          <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-300 font-display">完整过程列表：</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {correctPhrases.map((phrase, i) => (
                <span 
                  key={i} 
                  className="px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-200 font-mono text-sm border border-cyan-500/30"
                >
                  {phrase.text}
                </span>
              ))}
            </div>
          </div>

          {validationResult.missed.length === 0 && validationResult.extra.length === 0 ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <span className="text-green-300 font-display">完美！你正确识别了所有的过程。</span>
            </div>
          ) : (
            <div className="space-y-3">
              {getMissedPhrases().length > 0 && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <XCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <span className="text-yellow-300">遗漏了以下过程（或未完整选择）：</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {getMissedPhrases().map((phrase, i) => (
                        <span key={i} className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-200 font-mono text-sm">
                          {phrase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {getExtraPhrases().length > 0 && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <span className="text-red-300">以下不是过程：</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {getExtraPhrases().map((phrase, i) => (
                        <span key={i} className="px-2 py-1 rounded bg-red-500/20 text-red-200 font-mono text-sm">
                          {phrase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
